import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import { ProfileFindDto, type ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { ExternalUserAccessControlDto, type ExternalUserAccessControlInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-access-control.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { SecurityDtoKeysEnum } from '@zaoblako/shared/modules/security/enums/security-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import type { AccessCardFindInterface } from '@zaoblako/shared/modules/pacs/dtos/access-card/access-card-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { RoomAccessFindInterface } from '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';

import * as yup from 'yup';

/** External user response body */
export interface ExternalUserFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech columns
  /** External user creation date and time */
  created: Date;
  /** External user last update date and time */
  updated: Date;
  /** External user soft deletion date and time */
  deleted?: Date;
  /** User who created this external user */
  createdBy?: UserFindInterface;

  // Data columns
  /** Is external user banned */
  banned?: boolean;
  /** External user job title */
  job?: string;
  /** If `true` then external user will receive mailings */
  isMailingEnabled: boolean;
  /** PACS settings for this external user */
  pacsSettings?: RoomAccessFindInterface;
  /** Profile this external user belongs to */
  profile: ProfileFindInterface;
  /** Client this external user belongs to */
  client: ClientFindInterface;
  /** External user access control settings */
  accessControl?: ExternalUserAccessControlInterface;
  /** Counterparties this external user belongs to */
  contragents?: ContragentFindInterface[];
  /** Linked Tenant */
  tenant?: TenantFindInterface;
  /** Access cards of PACS module */
  accessCards?: AccessCardFindInterface[];
}

/** External user response body - DTO schema */
export const ExternalUserFindDto = SerdeDtoClass.create<ExternalUserFindInterface>(SecurityDtoKeysEnum.EXTERNAL_USER_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Дата создания',
    serializer: yup.date(),
  },
  updated: {
    label: 'Дата последнего изменения',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  banned: {
    sortable: true,
    optional: true,
    label: 'Забанен',
    serializer: yup.boolean(),
  },
  job: {
    sortable: true,
    optional: true,
    label: 'Должность',
    serializer: yup.string(),
  },
  isMailingEnabled: {
    sortable: true,
    label: 'Включать в рассылку',
    serializer: yup.boolean(),
  },
  pacsSettings: {
    optional: true,
    label: 'Настройки СКУД',
    nested: SecurityDtoKeysEnum.ROOM_ACCESS_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  profile: {
    sortable: true,
    label: 'Профиль',
    nested: ProfileFindDto,
    serializer: (_, nested) => nested,
  },
  client: {
    sortable: true,
    label: 'Клиент',
    nested: ClientDtoKeysEnum.CLIENT_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  accessControl: {
    optional: true,
    label: 'Права доступа',
    nested: ExternalUserAccessControlDto,
    serializer: (_, nested) => nested,
  },
  contragents: {
    optional: true,
    label: 'Контрагенты',
    nested: ClientDtoKeysEnum.CONTRAGENT_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  accessCards: {
    optional: true,
    label: 'Карты доступа',
    nested: PacsDtoKeysEnum.ACCESS_CARD_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
});
